import {
    Button,
    CloseButton,
    Dialog,
    Field,
    Input,
    Portal,
    Stack,
} from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { createWeddingEventCallable, createAdminUserAccount } from '../services/firebaseService'
import { toaster } from './ui/toaster'
import { useTranslation } from '../i18n'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function CreateWeddingDialog({ open, onClose, onCreated }) {
    const { t, lang } = useTranslation()
    const [couple, setCouple] = useState('')
    const [receptionStart, setReceptionStart] = useState('')
    const [receptionEnd, setReceptionEnd] = useState('')
    const [adminEmail, setAdminEmail] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [errors, setErrors] = useState({})

    const reset = useCallback(() => {
        setCouple('')
        setReceptionStart('')
        setReceptionEnd('')
        setAdminEmail('')
        setErrors({})
        setSubmitting(false)
    }, [])

    const handleOpenChange = useCallback((e) => {
        if (!e.open && !submitting) {
            reset()
            onClose?.()
        }
    }, [submitting, reset, onClose])

    const validate = useCallback(() => {
        const next = {}
        if (!couple.trim()) next.couple = t('wedding.coupleRequired')
        if (!receptionStart) next.receptionStart = t('wedding.startRequired')
        if (!receptionEnd) next.receptionEnd = t('wedding.endRequired')
        if (receptionStart && receptionEnd && receptionEnd <= receptionStart) {
            next.receptionEnd = t('wedding.endAfterStart')
        }
        if (!adminEmail.trim()) {
            next.adminEmail = t('wedding.adminEmailRequired')
        } else if (!EMAIL_RE.test(adminEmail.trim())) {
            next.adminEmail = t('wedding.emailInvalid')
        }
        setErrors(next)
        return Object.keys(next).length === 0
    }, [couple, receptionStart, receptionEnd, adminEmail, t])

    const handleSubmit = useCallback(async () => {
        if (!validate()) return
        setSubmitting(true)
        try {
            const eventId = await createWeddingEventCallable({
                name: couple.trim(),
                receptionStart,
                receptionEnd,
                lang,
            })
            try {
                await createAdminUserAccount({
                    email: adminEmail.trim(),
                    role: 'weddingadmin',
                    eventId,
                })
                toaster.create({ title: t('wedding.created'), status: 'success' })
            } catch (inviteErr) {
                console.error(inviteErr)
                toaster.create({
                    title: t('wedding.inviteFailed'),
                    description: t('wedding.inviteFailedDesc', { message: inviteErr.message || t('wedding.unknownError') }),
                    status: 'warning',
                })
            }
            reset()
            onCreated?.()
        } catch (err) {
            console.error(err)
            toaster.create({
                title: t('wedding.createFailed'),
                description: err.message,
                status: 'error',
            })
            setSubmitting(false)
        }
    }, [couple, receptionStart, receptionEnd, adminEmail, lang, validate, reset, onCreated, t])

    const disabled = submitting

    return (
        <Dialog.Root open={open} onOpenChange={handleOpenChange}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content>
                        <Dialog.Header>
                            <Dialog.Title>{t('wedding.createTitle')}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.Body>
                            <Stack gap={4}>
                                <Field.Root invalid={!!errors.couple} required>
                                    <Field.Label>{t('wedding.couple')}</Field.Label>
                                    <Input
                                        placeholder={t('wedding.couplePlaceholder')}
                                        value={couple}
                                        onChange={(e) => setCouple(e.target.value)}
                                        disabled={disabled}
                                    />
                                    <Field.HelperText>{t('wedding.coupleHelp')}</Field.HelperText>
                                    {errors.couple && <Field.ErrorText>{errors.couple}</Field.ErrorText>}
                                </Field.Root>

                                <Field.Root invalid={!!errors.receptionStart} required>
                                    <Field.Label>{t('wedding.receptionStart')}</Field.Label>
                                    <Input
                                        type="datetime-local"
                                        value={receptionStart}
                                        onChange={(e) => setReceptionStart(e.target.value)}
                                        disabled={disabled}
                                    />
                                    {errors.receptionStart && <Field.ErrorText>{errors.receptionStart}</Field.ErrorText>}
                                </Field.Root>

                                <Field.Root invalid={!!errors.receptionEnd} required>
                                    <Field.Label>{t('wedding.receptionEnd')}</Field.Label>
                                    <Input
                                        type="datetime-local"
                                        value={receptionEnd}
                                        onChange={(e) => setReceptionEnd(e.target.value)}
                                        disabled={disabled}
                                    />
                                    {errors.receptionEnd && <Field.ErrorText>{errors.receptionEnd}</Field.ErrorText>}
                                </Field.Root>

                                <Field.Root invalid={!!errors.adminEmail} required>
                                    <Field.Label>{t('wedding.adminEmail')}</Field.Label>
                                    <Input
                                        type="email"
                                        placeholder={t('wedding.adminEmailPlaceholder')}
                                        value={adminEmail}
                                        onChange={(e) => setAdminEmail(e.target.value)}
                                        disabled={disabled}
                                    />
                                    <Field.HelperText>{t('wedding.adminEmailHelp')}</Field.HelperText>
                                    {errors.adminEmail && <Field.ErrorText>{errors.adminEmail}</Field.ErrorText>}
                                </Field.Root>
                            </Stack>
                        </Dialog.Body>
                        <Dialog.Footer>
                            <Button variant="outline" onClick={() => handleOpenChange({ open: false })} disabled={disabled}>
                                {t('common.cancel')}
                            </Button>
                            <Button
                                colorPalette="brand"
                                onClick={handleSubmit}
                                loading={submitting}
                                loadingText={t('common.creating')}
                            >
                                {t('wedding.createButton')}
                            </Button>
                        </Dialog.Footer>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" disabled={disabled} />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    )
}
